import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { SITE } from "../data/site";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus("ok");
      setEmail("");
    } catch { setStatus("err"); }
  };

  return (
    <footer data-testid="site-footer" className="relative bg-indigo-gradient text-[#faf6ef] pt-24 pb-8 mt-16 rounded-t-[2.5rem] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #faf6ef 1px, transparent 1px), radial-gradient(circle at 80% 60%, #d98f2f 1px, transparent 1px)", backgroundSize: "40px 40px" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10 md:gap-12">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center overflow-hidden">
              <img src={SITE.logoUrl} alt="Indus Art" className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-serif text-2xl leading-tight">Indus Art</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#faf6ef]/60">Art &amp; Emporium</div>
            </div>
          </div>
          <p className="text-[#faf6ef]/70 text-sm leading-[1.7] max-w-xs">
            Manufacturer &amp; exporter of hand block printed handmade products from Jaipur, India. Since 2007.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <a href={SITE.instagramUrl} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-[#d98f2f] transition">
              <Instagram size={16} strokeWidth={1.8} />
            </a>
            <a href={SITE.pinterestUrl} target="_blank" rel="noreferrer" data-testid="footer-pinterest" className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center hover:bg-[#d98f2f] transition" aria-label="Pinterest">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0a12 12 0 00-4.37 23.17c-.1-.94-.2-2.43.04-3.47.22-.95 1.4-6.05 1.4-6.05s-.36-.72-.36-1.78c0-1.66.96-2.9 2.16-2.9 1.02 0 1.51.77 1.51 1.69 0 1.03-.66 2.57-1 4-.28 1.2.6 2.18 1.78 2.18 2.14 0 3.78-2.26 3.78-5.51 0-2.88-2.07-4.9-5.03-4.9-3.42 0-5.43 2.57-5.43 5.22 0 1.03.4 2.14.9 2.74.1.12.11.22.08.34l-.33 1.36c-.05.22-.17.27-.4.16-1.5-.7-2.43-2.88-2.43-4.64 0-3.78 2.74-7.25 7.91-7.25 4.15 0 7.38 2.96 7.38 6.92 0 4.13-2.6 7.45-6.21 7.45-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.34-1.49 3.13A12 12 0 1012 0z"/></svg>
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#d98f2f] mb-5">Explore</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/our-craft" className="hover:text-[#d98f2f] transition">Our Craft</Link></li>
            <li><Link to="/products" className="hover:text-[#d98f2f] transition">Products</Link></li>
            <li><Link to="/custom-orders" className="hover:text-[#d98f2f] transition">Custom Orders</Link></li>
            <li><Link to="/projects" className="hover:text-[#d98f2f] transition">Projects</Link></li>
            <li><Link to="/about" className="hover:text-[#d98f2f] transition">About</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#d98f2f] mb-5">Visit &middot; Write</div>
          <a href={SITE.mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-3 mb-4 group">
            <MapPin size={16} strokeWidth={1.8} className="mt-1 text-[#d98f2f] shrink-0" />
            <span className="text-sm leading-[1.7] text-[#faf6ef]/80 group-hover:text-[#d98f2f] transition">{SITE.address}</span>
          </a>
          <div className="flex items-center gap-3 mb-3">
            <Phone size={16} strokeWidth={1.8} className="text-[#d98f2f]" />
            <a href={`tel:${SITE.whatsapp}`} className="text-sm hover:text-[#d98f2f]">{SITE.phone}</a>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <Mail size={16} strokeWidth={1.8} className="text-[#d98f2f]" />
            <a href={`mailto:${SITE.email}`} className="text-sm hover:text-[#d98f2f]">{SITE.email}</a>
          </div>
          <div className="text-xs text-[#faf6ef]/55 mt-5">{SITE.responseTime}</div>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#d98f2f] mb-5">Trade Catalogue</div>
          <p className="text-sm text-[#faf6ef]/70 mb-5 leading-[1.7]">Quarterly lookbook for wholesale buyers.</p>
          <form onSubmit={subscribe} className="flex bg-white/10 backdrop-blur rounded-full pl-4 pr-1 py-1 border border-white/15">
            <input
              data-testid="newsletter-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@company.com"
              className="flex-1 bg-transparent py-2 text-sm placeholder:text-[#faf6ef]/40 focus:outline-none"
              required
            />
            <button data-testid="newsletter-submit" type="submit" className="rounded-full bg-[#d98f2f] hover:bg-[#faf6ef] hover:text-[#1e2a5a] w-10 h-10 flex items-center justify-center transition">
              <ArrowRight size={16} strokeWidth={2.2} />
            </button>
          </form>
          {status === "ok"  && <div data-testid="newsletter-success" className="text-xs text-[#d98f2f] mt-3">Subscribed. Thank you.</div>}
          {status === "err" && <div className="text-xs text-[#b4462a] mt-3">Couldn't subscribe.</div>}
        </div>
      </div>

      <div className="relative mt-16 pt-8 border-t border-[#faf6ef]/15 max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#faf6ef]/55">
        <div>© {new Date().getFullYear()} Indus Art &amp; Emporium. All rights reserved.</div>
        <div className="flex flex-wrap gap-6">
          <Link to="/terms" className="hover:text-[#d98f2f] transition" data-testid="footer-terms">Terms &amp; Conditions</Link>
          <Link to="/privacy" className="hover:text-[#d98f2f] transition" data-testid="footer-privacy">Privacy Policy</Link>
          <Link to="/sitemap" className="hover:text-[#d98f2f] transition" data-testid="footer-sitemap">Sitemap</Link>
          <span className="text-[#faf6ef]/35">GOTS · OEKO-TEX · SA8000 · Sedex</span>
        </div>
      </div>
    </footer>
  );
}
